/**
 * @internal — SANCTIONED SEALED PRIMITIVE. Do NOT import from route pages or via
 * the public `@/components/ui` barrel; these primitives are intentionally
 * excluded from it.
 *
 * FmFormShell + FmFormItem are the bounded, internal Form-context wrappers for
 * complex forms whose shape the sealed `FmForm` descriptor API cannot express:
 * multi-select / RangePicker / slider+number combos, custom ReactNode labels,
 * `mapsToNull` remaps, nested `FmAccordion` sections, conditional validators, and
 * multi-step flows. This is a GOVERNED path,
 * not a seam to eliminate: consumers bind the sealed `useFmForm()`
 * `FmFormHandle` (never a raw AntD `FormInstance`), and structural/style/
 * className props are omitted from the public type surface so pages cannot
 * recreate page-owned layout. FmForm-descriptor-expressible forms MUST use the
 * `FmForm` descriptor API instead (e.g. SimulationModal, migrated in a9g8o).
 *
 * Simple forms (flat text/number/select/checkbox with standard validation) MUST
 * use the `FmForm` descriptor API instead — e.g. SimulationModal (a9g8o) and
 * AccessTokenManager's create-token modal (jyhvz), both migrated off these
 * wrappers to data-only field descriptors.
 *
 * Consumers (all sealed via `useFmForm()`; none touch raw `Form.useForm`):
 *   - components/ui/FmField.tsx (DS primitive; composes FmFormItem)
 *   - components/stand/AddCorrectionDrawer.tsx (+ .parts.tsx)
 *   - components/auth/EnhancedRegisterForm.tsx
 */
import React from 'react';
import { Form } from 'antd';
import type { FormItemProps } from 'antd';
import type { Rule } from 'antd/es/form';
import type { FmFormDensity } from '../FmForm';
import type { FmFormHandle } from '../FmFormHooks';
import { formatRequiredMessage } from '../formValidation';
import { resolveFmFormInstance } from './fmFormBridge';
import { useLanguage } from '@/i18n';
import '../FmLayout.css';

type FmLegacyFieldsChangeHandler = NonNullable<
  React.ComponentProps<typeof Form>['onFieldsChange']
>;

export interface FmFormShellProps<Values = Record<string, unknown>> {
  /** Sealed handle from `useFmForm()`. Raw AntD `FormInstance` is never accepted. */
  form?: FmFormHandle<Values>;
  initialValues?: Record<string, unknown>;
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  density?: FmFormDensity;
  disabled?: boolean;
  testId?: string;
  onFieldsChange?: FmLegacyFieldsChangeHandler;
  children: React.ReactNode;
}

/**
 * @internal Raw AntD Form-context shell with DS form styling. Kept internal so
 * route pages cannot reach a children-accepting form via the public barrel.
 */
export function FmFormShell<Values = Record<string, unknown>>({
  form,
  initialValues,
  onSubmit,
  density = 'comfortable',
  disabled,
  testId,
  onFieldsChange,
  children,
}: FmFormShellProps<Values>): React.ReactElement {
  const [ownForm] = Form.useForm();
  const activeForm = form ? resolveFmFormInstance(form) : ownForm;

  return (
    <Form
      form={activeForm}
      layout="vertical"
      initialValues={initialValues}
      onFinish={(values) => onSubmit(values)}
      onFieldsChange={onFieldsChange}
      className={`fm-form fm-form--${density}`}
      data-disabled={disabled || undefined}
      data-testid={testId}
    >
      {children}
    </Form>
  );
}

/**
 * @internal Bounded Form.Item wrapper. Structural props (style, className,
 * labelCol/wrapperCol, noStyle, etc.) are deliberately omitted so this cannot
 * be used to recreate page-local form layout.
 */
export interface FmFormItemProps {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  help?: React.ReactNode;
  tooltip?: React.ReactNode;
  rules?: Rule[];
  valuePropName?: 'checked';
  validateTrigger?: FormItemProps['validateTrigger'];
  children: React.ReactNode;
}

export const FmFormItem: React.FC<FmFormItemProps> = ({
  name,
  label,
  required,
  help,
  tooltip,
  rules,
  valuePropName,
  validateTrigger,
  children,
}) => {
  const { t } = useLanguage();
  const computedRules: Rule[] = [
    ...(rules ?? []),
    ...(required
      ? [
          {
            required: true,
            message: formatRequiredMessage(
              t.common.ui.form.required,
              typeof label === 'string' ? label : name,
            ),
          },
        ]
      : []),
  ];

  return (
    <Form.Item
      name={name}
      label={label}
      // Labels read "Kohde", never AntD's default "Kohde :".
      colon={false}
      rules={computedRules}
      help={help}
      tooltip={tooltip}
      valuePropName={valuePropName}
      validateTrigger={validateTrigger}
      className="fm-form-item"
    >
      {children}
    </Form.Item>
  );
};
